import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from "lucide-react";

interface IPagination {
  setPage: (page: number) => void;
  page: number;
  totalPages?: number;
  totalItems?: number;
  paginationParams?: {
    page?: number;
    totalPages?: number;
    totalItems?: number;
    from?: number;
    to?: number;
  };
  className?: string;
  showFirstLast?: boolean;
}

const Pagination: React.FC<IPagination> = ({
  setPage,
  page,
  totalPages,
  totalItems,
  paginationParams,
  className = "",
  showFirstLast = true,
}) => {
  const activePage = Math.max(1, page || paginationParams?.page || 1);
  const total = Math.max(1, paginationParams?.totalPages || totalPages || 1);
  const totalRecords = paginationParams?.totalItems ?? totalItems ?? 0;
  const fromRecord =
    paginationParams?.from !== undefined
      ? paginationParams.from
      : totalRecords
      ? (activePage - 1) * 10 + 1
      : 0;
  const toRecord =
    paginationParams?.to !== undefined
      ? paginationParams.to
      : totalRecords
      ? Math.min(activePage * 10, totalRecords)
      : 0;

  const generatePages = (): (number | string)[] => {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    if (activePage <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push("dots-end");
      pages.push(total);
    } else if (activePage >= total - 3) {
      pages.push(1);
      pages.push("dots-start");
      for (let i = total - 4; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push("dots-start");
      pages.push(activePage - 1);
      pages.push(activePage);
      pages.push(activePage + 1);
      pages.push("dots-end");
      pages.push(total);
    }

    return pages;
  };

  const handlePageClick = (p: number) => {
    if (p >= 1 && p <= total && p !== activePage) {
      setPage(p);
    }
  };

  return (
    <div
      className={`py-4 px-1 sm:px-2 flex flex-col sm:flex-row items-center justify-between gap-4 select-none ${className}`}
    >
      {/* Information text & badge */}
      {totalRecords > 0 ? (
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-slate-50/90 border border-slate-200/80 px-4 py-2 rounded-2xl shadow-xs">
          <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
          <span>
            Showing <span className="font-black text-slate-800">{fromRecord}</span> -{" "}
            <span className="font-black text-slate-800">{toRecord}</span> of{" "}
            <span className="font-black text-slate-900">
              {totalRecords.toLocaleString()}
            </span>{" "}
            entries
          </span>
        </div>
      ) : (
        <div className="text-xs font-semibold text-slate-400 bg-slate-50/90 border border-slate-200/80 px-3.5 py-1.5 rounded-2xl">
          Page <span className="font-black text-slate-800">{activePage}</span> of{" "}
          <span className="font-black text-slate-800">{total}</span>
        </div>
      )}

      {/* Pagination Controls */}
      {total > 1 && (
        <nav
          aria-label="Pagination"
          className="flex items-center gap-1 bg-white p-1 rounded-2xl border border-slate-200/80 shadow-xs"
        >
          {/* Quick First Page Button */}
          {showFirstLast && (
            <button
              type="button"
              onClick={() => handlePageClick(1)}
              disabled={activePage <= 1}
              title="First Page"
              className="p-2 rounded-xl text-slate-400 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 disabled:cursor-not-allowed transition-all"
            >
              <ChevronsLeft size={16} />
            </button>
          )}

          {/* Previous Page Button */}
          <button
            type="button"
            onClick={() => handlePageClick(activePage - 1)}
            disabled={activePage <= 1}
            className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={15} />
            <span className="hidden md:inline">Prev</span>
          </button>

          {/* Numbered Page Buttons with Ellipsis */}
          <div className="flex items-center gap-1">
            {generatePages().map((item, idx) => {
              if (typeof item === "string") {
                return (
                  <span
                    key={`dots-${idx}`}
                    className="w-8 h-8 flex items-center justify-center text-slate-400"
                  >
                    <MoreHorizontal size={14} />
                  </span>
                );
              }

              const isCurrent = activePage === item;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => handlePageClick(item)}
                  className={`min-w-[34px] h-[34px] px-2 flex items-center justify-center rounded-xl text-xs font-black transition-all ${
                    isCurrent
                      ? "bg-[#012063] text-white shadow-md shadow-blue-950/20 scale-105"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>

          {/* Next Page Button */}
          <button
            type="button"
            onClick={() => handlePageClick(activePage + 1)}
            disabled={activePage >= total}
            className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 disabled:cursor-not-allowed transition-all"
          >
            <span className="hidden md:inline">Next</span>
            <ChevronRight size={15} />
          </button>

          {/* Quick Last Page Button */}
          {showFirstLast && (
            <button
              type="button"
              onClick={() => handlePageClick(total)}
              disabled={activePage >= total}
              title="Last Page"
              className="p-2 rounded-xl text-slate-400 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 disabled:cursor-not-allowed transition-all"
            >
              <ChevronsRight size={16} />
            </button>
          )}
        </nav>
      )}
    </div>
  );
};

export default Pagination;

export const PagenateWithoutBackEnd = ({
  array,
  page_change,
  pageNum,
  itemperPage = 10,
  totalItems,
}: any) => {
  const pageCount = Array.isArray(array)
    ? array.length
    : Math.ceil((totalItems || 0) / itemperPage) || 1;
  const currentPage = pageNum || 1;
  const currentTotal =
    totalItems || (Array.isArray(array) ? array.length * itemperPage : 0);
  const fromRecord = currentTotal ? (currentPage - 1) * itemperPage + 1 : 0;
  const toRecord = Math.min(currentPage * itemperPage, currentTotal);

  const generatePages = (): (number | string)[] => {
    if (pageCount <= 7) {
      return Array.from({ length: pageCount }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("dots-end");
      pages.push(pageCount);
    } else if (currentPage >= pageCount - 3) {
      pages.push(1);
      pages.push("dots-start");
      for (let i = pageCount - 4; i <= pageCount; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("dots-start");
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push("dots-end");
      pages.push(pageCount);
    }
    return pages;
  };

  return (
    <div className="py-4 px-1 sm:px-2 flex flex-col sm:flex-row items-center justify-between gap-4 select-none">
      {currentTotal > 0 ? (
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-slate-50/90 border border-slate-200/80 px-4 py-2 rounded-2xl shadow-xs">
          <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
          <span>
            Showing <span className="font-black text-slate-800">{fromRecord}</span> -{" "}
            <span className="font-black text-slate-800">{toRecord}</span> of{" "}
            <span className="font-black text-slate-900">
              {currentTotal.toLocaleString()}
            </span>{" "}
            entries
          </span>
        </div>
      ) : (
        <div className="text-xs font-semibold text-slate-400 bg-slate-50/90 border border-slate-200/80 px-3.5 py-1.5 rounded-2xl">
          Page <span className="font-black text-slate-800">{currentPage}</span> of{" "}
          <span className="font-black text-slate-800">{pageCount}</span>
        </div>
      )}

      {pageCount > 1 && (
        <nav
          aria-label="Pagination"
          className="flex items-center gap-1 bg-white p-1 rounded-2xl border border-slate-200/80 shadow-xs"
        >
          <button
            type="button"
            onClick={() => page_change(0)}
            disabled={currentPage <= 1}
            title="First Page"
            className="p-2 rounded-xl text-slate-400 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 disabled:cursor-not-allowed transition-all"
          >
            <ChevronsLeft size={16} />
          </button>

          <button
            type="button"
            onClick={() => page_change(Math.max(0, currentPage - 2))}
            disabled={currentPage <= 1}
            className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={15} />
            <span className="hidden md:inline">Prev</span>
          </button>

          <div className="flex items-center gap-1">
            {generatePages().map((item, idx) => {
              if (typeof item === "string") {
                return (
                  <span
                    key={`dots-${idx}`}
                    className="w-8 h-8 flex items-center justify-center text-slate-400"
                  >
                    <MoreHorizontal size={14} />
                  </span>
                );
              }
              const isCurrent = currentPage === item;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => page_change(item - 1)}
                  className={`min-w-[34px] h-[34px] px-2 flex items-center justify-center rounded-xl text-xs font-black transition-all ${
                    isCurrent
                      ? "bg-[#012063] text-white shadow-md shadow-blue-950/20 scale-105"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => page_change(Math.min(pageCount - 1, currentPage))}
            disabled={currentPage >= pageCount}
            className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 disabled:cursor-not-allowed transition-all"
          >
            <span className="hidden md:inline">Next</span>
            <ChevronRight size={15} />
          </button>

          <button
            type="button"
            onClick={() => page_change(pageCount - 1)}
            disabled={currentPage >= pageCount}
            title="Last Page"
            className="p-2 rounded-xl text-slate-400 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 disabled:cursor-not-allowed transition-all"
          >
            <ChevronsRight size={16} />
          </button>
        </nav>
      )}
    </div>
  );
};
